
import { useEffect, useRef } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

type EventType = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

type SubscriptionOptions = {
  table: string;
  event?: EventType;
  schema?: string;
  filter?: string;
  callback: (payload: any) => void;
};

// Helper to create a unique channel ID
const createChannelId = (table: string, event: EventType, filter?: string): string => {
  return `${table}_${event}${filter ? `_${filter}` : ''}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Creates and manages a Supabase realtime subscription
 * @returns A function to subscribe and a function to unsubscribe
 */
export const createRealtimeSubscription = () => {
  let channels: RealtimeChannel[] = [];
  
  /**
   * Subscribe to realtime changes
   * @param options - Configuration options for the subscription
   * @returns The RealtimeChannel instance
   */
  const subscribe = (options: SubscriptionOptions): RealtimeChannel => {
    const { table, event = '*', schema = 'public', filter, callback } = options;
    
    // Create a unique channel ID
    const channelId = createChannelId(table, event, filter);
    
    // Create the subscription
    const channel = supabase
      .channel(channelId)
      .on(
        'postgres_changes' as any, // Type assertion to fix type error
        {
          event,
          schema,
          table,
          ...(filter ? { filter } : {})
        },
        (payload) => {
          callback(payload);
        }
      )
      .subscribe();
    
    // Store the channel reference
    channels.push(channel);
    
    return channel;
  };
  
  /**
   * Unsubscribe from all channels
   */
  const unsubscribe = () => {
    channels.forEach(channel => {
      supabase.removeChannel(channel);
    });
    channels = [];
  };
  
  return { subscribe, unsubscribe };
};

/**
 * React hook to subscribe to realtime changes
 * @param options - Configuration options for the subscription
 */
export const useRealtimeSubscription = (options: SubscriptionOptions) => {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const { subscribe, unsubscribe } = createRealtimeSubscription();
    
    // Subscribe when the component mounts
    channelRef.current = subscribe({
      ...options,
      callback: (payload) => {
        // Call the original callback
        options.callback(payload);
        
        // Also invalidate relevant query cache based on table
        const tableQueryMap: Record<string, string[]> = {
          'employees': ['employees'],
          'departments': ['departments'],
          'mfo_items': ['mfoItems'],
          'kanban_columns': ['kanban'],
          'kanban_cards': ['kanban'],
          'notifications': ['notifications']
        };
        
        const queriesToInvalidate = tableQueryMap[options.table] || [];
        queriesToInvalidate.forEach(queryKey => {
          queryClient.invalidateQueries({ queryKey: [queryKey] });
        });
      }
    });
    
    // Unsubscribe when the component unmounts
    return () => {
      unsubscribe();
      channelRef.current = null;
    };
  }, [options.table, options.event, options.filter, queryClient]);

  return channelRef.current;
};

// Setup realtime updates for Kanban with automatic query cache invalidation
export const setupRealtimeKanban = (callback: () => void): (() => void) => {
  const { subscribe, unsubscribe } = createRealtimeSubscription();
  
  // Maintain references to the subscription channels
  const channels = [];
  
  // Subscribe to kanban_cards changes
  const cardsChannel = subscribe({
    table: 'kanban_cards',
    event: '*',
    callback: () => {
      // Throttle callback to prevent multiple rapid refreshes
      if (callback) {
        callback();
      }
    }
  });
  channels.push(cardsChannel);
  
  // Subscribe to kanban_columns changes
  const columnsChannel = subscribe({
    table: 'kanban_columns',
    event: '*',
    callback: () => {
      if (callback) {
        callback();
      }
    }
  });
  channels.push(columnsChannel);
  
  // Return a function to unsubscribe from all channels
  return () => {
    unsubscribe();
  };
};

// Add function to check for approaching deadlines
export const checkForApproachingDeadlines = async () => {
  const today = new Date();
  const oneWeekLater = new Date(today);
  oneWeekLater.setDate(today.getDate() + 7);
  
  const formattedDate = oneWeekLater.toISOString().split('T')[0];
  
  // Fetch MFOs with due dates within the next 7 days
  const { data, error } = await supabase
    .from('mfo_items')
    .select('id, code, title, due_date')
    .lte('due_date', formattedDate)
    .gt('due_date', today.toISOString().split('T')[0])
    .not('status', 'eq', 'completed');
    
  if (error) {
    console.error('Error checking for approaching deadlines:', error);
    return [];
  }
  
  return data || [];
};
